import React, { useState } from "react";
import { Modal, Button, Form, Alert, Badge } from "react-bootstrap";
import api from "../../services/api";
import { MassDeleteResult } from "../types/admin.types";
import { useLanguage } from "../../i18n/LanguageContext";

interface MassDeleteModalProps {
  show: boolean;
  onHide: () => void;
  onSuccess: () => void;
}

const parseIds = (raw: string): string[] =>
  Array.from(
    new Set(
      raw
        .split(/[\s,;|/\\]+/)
        .map((s) => s.trim())
        .filter((s) => s.length > 0),
    ),
  );

const MassDeleteModal: React.FC<MassDeleteModalProps> = ({
  show,
  onHide,
  onSuccess,
}) => {
  const { t, dir } = useLanguage();
  const [input, setInput] = useState("");
  const [parsed, setParsed] = useState<string[]>([]);
  const [confirmed, setConfirmed] = useState(false);
  const [results, setResults] = useState<MassDeleteResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleInputChange = (val: string) => {
    setInput(val);
    setParsed(parseIds(val));
    setConfirmed(false);
    setResults(null);
    setError("");
  };

  const removeId = (id: string) => {
    const updated = parsed.filter((x) => x !== id);
    setParsed(updated);
    setInput(updated.join(" "));
    setConfirmed(false);
  };

  const handleDelete = async () => {
    if (!parsed.length) return;
    setLoading(true);
    setError("");
    try {
      const r = await api.delete("/users/mass-delete", {
        data: { employee_ids: parsed },
      });
      setResults(r.data);
      setInput("");
      setParsed([]);
      setConfirmed(false);
      onSuccess();
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to mass delete users");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setInput("");
    setParsed([]);
    setConfirmed(false);
    setResults(null);
    setError("");
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" dir={dir}>
      <Modal.Header closeButton>
        <Modal.Title>🗑 {t("massDeleteUsers")}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {error && (
          <Alert
            variant="danger"
            dismissible
            onClose={() => setError("")}
            className="mb-3"
          >
            {error}
          </Alert>
        )}

        {results ? (
          <div>
            {results.deleted.length > 0 && (
              <Alert variant="success" className="mb-3">
                <strong>
                  ✅ {t("delete")} {results.deleted.length}{" "}
                  {results.deleted.length !== 1
                    ? t("userAccounts")
                    : t("userAccount")}
                  :
                </strong>
                <div className="mt-2 d-flex flex-wrap gap-2">
                  {results.deleted.map((id) => (
                    <Badge
                      key={id}
                      bg="success"
                      className="px-2 py-1"
                      style={{ fontSize: "13px" }}
                    >
                      {id}
                    </Badge>
                  ))}
                </div>
              </Alert>
            )}
            {results.not_found.length > 0 && (
              <Alert variant="warning" className="mb-0">
                <strong>
                  ⚠️ {t("notFound")} ({results.not_found.length}):
                </strong>
                <div className="mt-2 d-flex flex-wrap gap-2">
                  {results.not_found.map((id) => (
                    <Badge
                      key={id}
                      bg="warning"
                      text="dark"
                      className="px-2 py-1"
                      style={{ fontSize: "13px" }}
                    >
                      {id}
                    </Badge>
                  ))}
                </div>
              </Alert>
            )}
          </div>
        ) : (
          <div>
            <p className="text-muted small mb-3">{t("massDeleteDesc")}</p>

            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">
                {t("employeeNumbers")}
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={5}
                value={input}
                onChange={(e) => handleInputChange(e.target.value)}
                placeholder={t("massDeletePastePlaceholder")}
                style={{
                  fontFamily: "monospace",
                  fontSize: "14px",
                  resize: "vertical",
                }}
                autoFocus
              />
            </Form.Group>

            {parsed.length > 0 && (
              <div
                className="border rounded p-3 mb-3"
                style={{ background: "#f8f9fa" }}
              >
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span
                    className="fw-semibold small text-muted text-uppercase"
                    style={{ letterSpacing: "0.05em" }}
                  >
                    {parsed.length}{" "}
                    {parsed.length !== 1
                      ? t("employeesRecognized")
                      : t("employeeRecognized")}
                  </span>
                  <Button
                    variant="link"
                    size="sm"
                    className="p-0 text-muted"
                    style={{ fontSize: "12px" }}
                    onClick={() => handleInputChange("")}
                  >
                    {t("clearAll")}
                  </Button>
                </div>
                <div className="d-flex flex-wrap gap-2">
                  {parsed.map((id) => (
                    <Badge
                      key={id}
                      bg="danger"
                      className="px-2 py-1"
                      style={{
                        fontSize: "13px",
                        cursor: "default",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "5px",
                      }}
                    >
                      {id}
                      <span
                        title={t("delete")}
                        style={{
                          cursor: "pointer",
                          opacity: 0.75,
                          fontWeight: "bold",
                        }}
                        onClick={() => removeId(id)}
                      >
                        ×
                      </span>
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {parsed.length > 0 && (
              <Alert variant="danger" className="mb-0">
                <Form.Check
                  type="checkbox"
                  id="mass-delete-confirm"
                  checked={confirmed}
                  onChange={(e) => setConfirmed(e.target.checked)}
                  label={
                    <span>
                      {t("massDeleteConfirm")}{" "}
                      <strong>
                        {parsed.length}{" "}
                        {parsed.length !== 1
                          ? t("userAccounts")
                          : t("userAccount")}
                      </strong>{" "}
                      {t("andCannotBeUndone")}
                    </span>
                  }
                />
              </Alert>
            )}
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          {results ? t("close") : t("cancel")}
        </Button>
        {!results && (
          <Button
            variant="danger"
            disabled={!confirmed || loading || parsed.length === 0}
            onClick={handleDelete}
          >
            {loading
              ? t("deleting")
              : `${t("deleteUsers")} ${parsed.length > 0 ? parsed.length : ""} ${parsed.length !== 1 ? t("userAccounts") : t("userAccount")}`}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default MassDeleteModal;
